import { Fragment, Suspense, lazy, useState, useEffect } from 'react';
import { Outlet, NavLink, matchPath, useLocation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon,
  ChartBarIcon,
  ChartPieIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { Logo } from '@/components/common/Logo';
import { ChainSelector } from '@/components/common/ChainSelector';
import { ProfileDropdown } from '@/components/common/ProfileDropdown';
import { SearchModal } from '@/components/common/SearchModal';
import { BetaDisclaimerGate } from '@/components/common/BetaDisclaimerModal';
import { useAuth, useAdminUI } from '@/stores';
import { ROUTES } from '@/config/routes';
import { cn } from '@/utils/cn';

interface NavSubcategory {
  name: string;
  href: string;
  authOnly?: boolean;
}

interface NavItem {
  name: string;
  href: string;
  icon: typeof ChartBarIcon;
  subcategories: NavSubcategory[];
}

// Public landing for signed-out visitors — lazy so it stays out of the app bundle
const Landing = lazy(() => import('@/pages/Landing/Landing'));

const navigation: NavItem[] = [
  {
    name: 'Trading',
    href: ROUTES.TRADING.ROOT,
    icon: ChartBarIcon,
    subcategories: [
      { name: 'Overview', href: ROUTES.TRADING.ROOT },
      { name: 'Strategies', href: ROUTES.TRADING.STRATEGIES },
      { name: 'All Performance', href: ROUTES.TRADING.ADMIN_STRATEGY_PERFORMANCE },
    ],
  },
  {
    name: 'Data Explorer',
    href: ROUTES.DATA_EXPLORER.ROOT,
    icon: ChartPieIcon,
    subcategories: [
      { name: 'Wallet explorer', href: ROUTES.DATA_EXPLORER.ROOT },
      { name: 'Token explorer', href: ROUTES.DATA_EXPLORER.TOKEN_EXPLORER },
      { name: 'SOL Tokens', href: ROUTES.DATA_EXPLORER.SOL_TOKEN_EXPLORER },
      { name: 'My Lists', href: ROUTES.DATA_EXPLORER.MY_LISTS, authOnly: true },
    ],
  },
  {
    name: 'AI Chat',
    href: ROUTES.AI_CHAT.ROOT,
    icon: ChatBubbleLeftRightIcon,
    subcategories: [],
  },
];

export const AppLayout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const isFullWidthContentRoute = !!matchPath(ROUTES.TRADING.STRATEGY_EDITOR, location.pathname);

  // Navigation exists only in the admin experience (admin build + admin user).
  // The general-user app has a single destination — the trading overview — so
  // it renders no top-nav links, no sub-bar, and no mobile bottom nav; token
  // pages are reached by clicking tokens directly.
  const adminUI = useAdminUI();
  const visibleNav = adminUI ? navigation : [];
  const activeNavItem = visibleNav.find((item) => location.pathname.startsWith(item.href));
  const visibleSubcats = (activeNavItem?.subcategories ?? []).filter(
    (sub) => isAuthenticated || !sub.authOnly
  );

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (authLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Suspense
        fallback={
          <div className="flex h-screen items-center justify-center bg-abyss">
            <div className="spinner text-violet-400" />
          </div>
        }
      >
        <Landing />
      </Suspense>
    );
  }

  return (
    <>
      {/* Search Modal */}
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Beta disclaimer — blocks the app until the current version is accepted */}
      <BetaDisclaimerGate />

      <div className="flex h-full flex-col overflow-hidden bg-gray-50 dark:bg-transparent">
        {/* Desktop Navigation */}
        <header className="sticky top-0 z-40 hidden border-b bg-white/80 backdrop-blur-xl dark:border-white/[0.06] dark:bg-abyss-100/70 lg:block">
          <nav className="mx-auto flex h-14 max-w-[1920px] items-center justify-between px-4">
            {/* Left section: Logo + Nav links */}
            <div className="flex items-center gap-6">
              <Logo />

              {/* Navigation links */}
              <div className="flex items-center gap-1">
                {visibleNav.map((item) => {
                  const isActive = location.pathname.startsWith(item.href);
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={cn(
                        'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-200',
                        isActive
                          ? 'bg-gray-100 text-gray-900 dark:border dark:border-violet-400/20 dark:bg-violet-500/15 dark:text-white dark:shadow-inner-edge'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/[0.05] dark:hover:text-white'
                      )}
                    >
                      <item.icon className="h-4 w-4" />
                      {item.name}
                    </NavLink>
                  );
                })}
              </div>
            </div>

            {/* Right section: Search + Profile + Chain */}
            <div className="flex items-center gap-2">
              {/* Search button */}
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-1.5 rounded-lg border bg-gray-50 px-3 py-1.5 text-sm text-gray-500 transition-colors hover:bg-gray-100 dark:border-white/[0.07] dark:bg-white/[0.04] dark:text-gray-400 dark:hover:border-white/[0.14] dark:hover:bg-white/[0.08]"
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
                <span className="hidden xl:inline">Search</span>
                <kbd className="ml-auto hidden rounded border bg-gray-100 px-1.5 py-0.5 text-xs dark:border-white/10 dark:bg-white/[0.06] xl:inline">
                  ⌘K
                </kbd>
              </button>

              {/* Profile dropdown */}
              <ProfileDropdown />

              {/* Chain selector */}
              <ChainSelector />
            </div>
          </nav>

          {/* Subcategories row - Desktop only (hidden when there's a single choice) */}
          {visibleSubcats.length > 1 && (
            <div className="border-b bg-gray-50 dark:border-white/[0.05] dark:bg-abyss-200/50">
              <div className="mx-auto flex max-w-[1920px] items-center gap-1 px-4 py-1.5">
                {visibleSubcats.map((subcat) => {
                  const isActive = location.pathname === subcat.href;
                  return (
                    <NavLink
                      key={subcat.name}
                      to={subcat.href}
                      className={cn(
                        'rounded-lg px-3 py-1 text-sm font-medium transition-all duration-200',
                        isActive
                          ? 'bg-white text-gray-900 shadow-sm dark:bg-violet-500/15 dark:text-violet-100 dark:shadow-inner-edge'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/[0.04] dark:hover:text-white'
                      )}
                    >
                      {subcat.name}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          )}
        </header>

        {/* Mobile Navigation - Top Bar */}
        <header className="sticky top-0 z-40 border-b bg-white/80 backdrop-blur-xl dark:border-white/[0.06] dark:bg-abyss-100/70 lg:hidden">
          <div className="flex h-14 items-center justify-between px-3">
            {/* Left: Search icon */}
            <button
              onClick={() => setSearchOpen(true)}
              className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/[0.06]"
            >
              <MagnifyingGlassIcon className="h-5 w-5" />
            </button>

            {/* Center: Logo */}
            <Logo variant="icon" />

            {/* Right: Chain selector (user app — no bottom bar to host it) + Hamburger */}
            <div className="flex items-center gap-1">
              {!adminUI && (
                <div className="h-9">
                  <ChainSelector variant="compact" />
                </div>
              )}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/[0.06]"
              >
                <Bars3Icon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </header>

        {/* Mobile Navigation - Bottom Tab Bar (admin experience only; the
            general-user app has a single destination, so the bar is removed
            entirely to free up space) */}
        {adminUI && (
          <nav className="fixed bottom-0 left-0 right-0 z-40 border-t bg-white/80 backdrop-blur-xl dark:border-white/[0.06] dark:bg-abyss-100/80 lg:hidden">
            <div className="grid h-14 grid-cols-4">
              {visibleNav.map((item) => {
                const isActive = location.pathname.startsWith(item.href);
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'flex flex-col items-center justify-center gap-0.5 text-xs font-medium transition-colors',
                      isActive
                        ? 'text-gray-900 dark:text-gray-100'
                        : 'text-gray-500 dark:text-gray-400'
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.name}</span>
                  </NavLink>
                );
              })}

              {/* Chain selector as 4th tab */}
              <ChainSelector variant="compact" menuDirection="up" />
            </div>
          </nav>
        )}

        {/* Mobile Menu Drawer */}
        <Transition show={mobileMenuOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMobileMenuOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
            </Transition.Child>

            <div className="fixed inset-0 flex justify-end">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="relative w-full max-w-sm">
                  <div className="flex h-full flex-col overflow-y-auto border-l bg-white dark:border-gray-800 dark:bg-gray-900">
                    {/* Header */}
                    <div className="flex h-14 items-center justify-between border-b px-3 dark:border-gray-800">
                      <Logo />
                      <button
                        onClick={() => setMobileMenuOpen(false)}
                        className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/[0.06]"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 px-3 py-4">
                      <ProfileDropdown variant="list" />
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>

        {/* Main Content */}
        <main className={cn('min-h-0 flex-1 overflow-y-auto', adminUI ? 'pb-14 lg:pb-0' : 'pb-0')}>
          <div className={cn(isFullWidthContentRoute ? 'w-full' : 'mx-auto max-w-[1920px]')}>
            {/* Subcategories - Mobile only (hidden when there's a single choice) */}
            {visibleSubcats.length > 1 && (
              <div className="border-b bg-white dark:border-gray-800 dark:bg-gray-900 lg:hidden">
                <div className="flex gap-1 overflow-x-auto p-3">
                  {visibleSubcats.map((subcat) => {
                    const isActive = location.pathname === subcat.href;
                    return (
                      <NavLink
                        key={subcat.name}
                        to={subcat.href}
                        className={cn(
                          'whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-gray-800 text-white dark:bg-violet-500/20 dark:text-violet-100'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
                        )}
                      >
                        {subcat.name}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            )}

            <Outlet />
          </div>
        </main>
      </div>
    </>
  );
};
